import React, { useState } from 'react';
import { X, ArrowLeft, ArrowRight, AlertCircle } from 'lucide-react';
import { supabase, Database } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { WizardStep } from './WizardStep';
import { WelcomeStep } from './wizard-steps/WelcomeStep';
import { ChildSelectionStep } from './wizard-steps/ChildSelectionStep';
import { CurriculumSelectionStep } from './wizard-steps/CurriculumSelectionStep';
import { LearningProfileStep } from './wizard-steps/LearningProfileStep';
import { ActivityPlanStep } from './wizard-steps/ActivityPlanStep';
import { BatchGenerationStep } from './wizard-steps/BatchGenerationStep';
import { CompletionStep } from './wizard-steps/CompletionStep';
import { useWizardState } from '../hooks/useWizardState';
import { CurriculumSelection } from '../lib/curriculum-systems';

type Child = Database['public']['Tables']['children']['Row'];

export interface AIActivityWizardProps {
  onClose: () => void;
  initialChild?: Child;
  mode?: 'new-child' | 'existing-child';
}

export interface WizardData {
  mode: 'new-child' | 'existing-child';
  childData?: {
    name: string;
    age: number;
    avatar_color: string;
    learning_preferences?: string[];
    strengths?: string[];
    challenges?: string[];
  };
  selectedChild?: Child;
  curriculumSelection?: CurriculumSelection;
  curriculumStandards?: {
    learningObjectives: string[];
    subjectAreas: string[];
    assessmentCriteria: string[];
  };
  learningProfile: {
    curriculumTargets: string[];
    learningObjectives: string[];
    customInstructions: string;
    accommodations: string[];
  };
  activityPlan: {
    subjects: Array<{
      subject: string;
      count: number;
    }>;
    totalActivities: number;
    difficulty?: string;
  };
  generatedActivities: Array<{
    id: string;
    subject: string;
    title: string;
    status: 'pending' | 'generating' | 'completed' | 'error';
    error?: string;
  }>;
  createdChild?: Child;
}

const WIZARD_STEPS = [
  { id: 'welcome', title: 'Welcome', component: WelcomeStep },
  { id: 'child-selection', title: 'Child Setup', component: ChildSelectionStep },
  { id: 'curriculum-selection', title: 'Curriculum', component: CurriculumSelectionStep },
  { id: 'learning-profile', title: 'Learning Profile', component: LearningProfileStep },
  { id: 'activity-plan', title: 'Activity Plan', component: ActivityPlanStep },
  { id: 'generation', title: 'Generation', component: BatchGenerationStep },
  { id: 'completion', title: 'Complete', component: CompletionStep },
];

export function AIActivityWizard({ onClose, initialChild, mode = 'new-child' }: AIActivityWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  
  console.log('AIActivityWizard initialized', { mode, initialChild: !!initialChild });
  
  const {
    wizardData,
    updateWizardData,
    canProceedToNext,
    resetWizard,
  } = useWizardState({
    mode,
    selectedChild: initialChild,
  });

  const currentStepData = WIZARD_STEPS[currentStep];
  const StepComponent = currentStepData.component;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === WIZARD_STEPS.length - 1;
  const isGenerationStep = currentStepData.id === 'generation';

  const handleNext = async () => {
    // Special handling for child-selection step in new-child mode
    if (currentStepData.id === 'child-selection' && 
        wizardData.mode === 'new-child' && 
        wizardData.childData && 
        !wizardData.createdChild) {
      
      try {
        console.log('Creating child before proceeding to next step...');
        const createdChild = await createChildFromWizardData(wizardData.childData);
        
        updateWizardData({ createdChild });
        console.log('Child created successfully, proceeding to next step');
      } catch (error) {
        console.error('Error creating child:', error);
        setError(error instanceof Error ? error.message : 'Failed to create child profile');
        return; // Don't proceed to next step if child creation fails
      }
    }
    
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    if (isGenerating) {
      const confirmClose = window.confirm(
        'Activity generation is in progress. Are you sure you want to close? This will cancel the generation process.'
      );
      if (!confirmClose) return;
    }
    
    resetWizard();
    onClose();
  };

  const createChildFromWizardData = async (childData: WizardData['childData']): Promise<Database['public']['Tables']['children']['Row']> => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    console.log('Creating child with data:', childData);

    const insertData = {
      name: childData.name,
      age: childData.age,
      avatar_color: childData.avatar_color,
      parent_id: user.id
    };

    const { data: createdChild, error } = await supabase
      .from('children')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Child creation error:', error);
      throw error;
    }

    console.log('Child created successfully:', createdChild);
    return createdChild;
  };

  const handleStepComplete = (stepData: Partial<WizardData>) => {
    console.log('Step completed:', currentStepData.id, stepData);
    setError(null); // Clear any previous errors
    updateWizardData(stepData);
    
    // Auto-advance for certain steps, but not generation or completion
    if (currentStepData.id === 'welcome' || 
        (currentStepData.id === 'child-selection' && stepData.selectedChild)) {
      setTimeout(() => handleNext(), 100);
    }
  };

  const getStepStatus = (stepIndex: number) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'current';
    return 'pending';
  };

  return (
    <div className="fixed inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">AI Activity Wizard</h1>
              <p className="text-white/80 text-sm">
                {mode === 'new-child' ? 'Create a new child and generate personalized activities' : 'Generate multiple activities for your child'}
              </p>
            </div>
            {!isGenerating && (
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-border bg-muted">
          <div className="flex items-center justify-between">
            {WIZARD_STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    getStepStatus(index) === 'completed'
                      ? 'bg-success text-white'
                      : getStepStatus(index) === 'current'
                      ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {index + 1}
                </div>
                <div className="ml-3 hidden md:block">
                  <div
                    className={`text-sm font-medium ${
                      getStepStatus(index) === 'current' ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {step.title}
                  </div>
                </div>
                {index < WIZARD_STEPS.length - 1 && (
                  <div className="hidden md:block mx-4 h-0.5 w-12 bg-border"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 p-4 bg-danger-light border border-danger rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-danger mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-danger-dark">Error</h4>
                <p className="text-sm text-danger mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto">
          <WizardStep
            stepId={currentStepData.id}
            title={currentStepData.title}
            isActive={true}
          >
            <StepComponent
              wizardData={wizardData}
              onStepComplete={handleStepComplete}
              onDataUpdate={updateWizardData}
              onNext={handleNext}
              onPrevious={handlePrevious}
              setIsGenerating={setIsGenerating}
            />
          </WizardStep>
        </div>

        {/* Navigation Footer */}
        {!isGenerationStep && (
          <div className="px-6 py-4 border-t border-border bg-muted">
            <div className="flex justify-between">
              <button
                onClick={handlePrevious}
                disabled={isFirstStep}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  isFirstStep
                    ? 'text-muted-foreground cursor-not-allowed'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>

              <button
                onClick={isLastStep ? handleClose : handleNext}
                disabled={!canProceedToNext(currentStepData.id)}
                className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-colors ${
                  canProceedToNext(currentStepData.id)
                    ? 'bg-primary text-white hover:bg-primary-dark'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                <span>{isLastStep ? 'Finish' : 'Next'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}